<script lang="ts">
  import { useThrelte } from '@threlte/core'
  import {
    Box3,
    Group,
    Mesh,
    MeshBasicMaterial,
    Plane,
    Raycaster,
    Vector2,
    Vector3,
  } from 'three'
  import { get } from 'svelte/store'
  import { cameraControls } from '../../stores/camera'
  import { sceneMode } from '../../stores/scene-mode'
  import { pendingPlacement } from '../../stores/placement'
  import { useMouseEvents } from '../../hooks'
  import useCanvasSelection from '../../hooks/canvas-selection'
  import { ThreeItemKeys } from '../../constants/three'

  const { scene, renderer } = useThrelte()
  const { clear: clearSelection } = useCanvasSelection()
  const raycaster = new Raycaster()
  const ground = new Plane(new Vector3(0, 0, 1), 0)

  const ghostMaterial = new MeshBasicMaterial({
    color: 0x3b82f6,
    transparent: true,
    opacity: 0.6,
    depthTest: false,
  })

  let ghost: Group | null = null
  let layout: { group: Group, offset: Vector3 }[] = []

  function computeLayout(groups: Group[]) {
    const margin = 0.05
    const widths: number[] = []
    let total = 0

    for (const group of groups) {
      group.updateWorldMatrix(false, true)
      const bbox = new Box3().setFromObject(group)
      const width = bbox.max.x - bbox.min.x
      widths.push(width)
      total += width
    }
    total += margin * Math.max(groups.length - 1, 0)

    let cursor = -total / 2
    return groups.map((group, i) => {
      const offsetX = cursor + widths[i] / 2
      cursor += widths[i] + margin
      return { group, offset: new Vector3(offsetX, 0, 0) }
    })
  }

  function buildGhost() {
    const root = new Group()
    root.userData[ThreeItemKeys.TRANSIENT] = true
    for (const { group, offset } of layout) {
      const clone = group.clone(true)
      clone.position.set(offset.x, offset.y, group.position.z)
      clone.traverse((obj) => {
        if (obj instanceof Mesh) obj.material = ghostMaterial
        obj.userData = {}
      })
      root.add(clone)
    }
    return root
  }

  function intersectGround(e: MouseEvent): Vector3 | null {
    const cc = get(cameraControls)
    if (!cc?.camera) return null

    const rect = renderer.domElement.getBoundingClientRect()
    const pointer = new Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    )
    raycaster.setFromCamera(pointer, cc.camera)

    const hit = new Vector3()
    return raycaster.ray.intersectPlane(ground, hit) ? hit : null
  }

  function onPointerMove(e: MouseEvent) {
    if (!ghost) return
    const hit = intersectGround(e)
    if (hit) ghost.position.set(hit.x, hit.y, 0)
  }

  async function onClick(e: MouseEvent) {
    if (!renderer.domElement.parentElement?.contains(e.target as Node)) return

    const placement = get(pendingPlacement)
    if (!placement) return

    const hit = intersectGround(e)
    if (!hit) return

    for (const { group, offset } of layout) {
      group.position.set(hit.x + offset.x, hit.y + offset.y, group.position.z)
      scene.add(group)
    }

    const { onPlaced } = placement
    disposeGhost()
    pendingPlacement.set(null)
    await onPlaced()
  }

  function disposeGhost() {
    if (ghost) {
      ghost.traverse((obj) => {
        if (obj instanceof Mesh) obj.geometry.dispose()
      })
      ghost.removeFromParent()
      ghost = null
    }
    layout = []
  }

  function discardPlacement() {
    const placement = get(pendingPlacement)
    placement?.groups.forEach(group =>
      group.traverse((obj) => {
        if (obj instanceof Mesh) obj.geometry.dispose()
      }),
    )
    disposeGhost()
    pendingPlacement.set(null)
  }

  // Active placement: set up ghost and mouse listeners
  $effect(() => {
    const placement = $pendingPlacement
    if (!placement?.groups.length) return

    clearSelection()
    layout = computeLayout(placement.groups)
    ghost = buildGhost()
    scene.add(ghost)

    const mouseOff = useMouseEvents({ onPointerMove, onClick })

    return () => {
      mouseOff()
      disposeGhost()
    }
  })

  // Switch to placement mode when a placement is pending
  $effect(() => {
    if ($pendingPlacement?.groups.length) {
      sceneMode.set('placement')
    }
  })

  // Switch back to default when placement ends while still in placement mode
  // Uses get(sceneMode) to avoid tracking sceneMode as a dependency
  $effect(() => {
    if (!$pendingPlacement && get(sceneMode) === 'placement') {
      sceneMode.set('default')
    }
  })

  // Discard ghost when user switches away from placement mode
  // Uses get(pendingPlacement) to avoid this effect firing when placement is first set
  $effect(() => {
    if ($sceneMode !== 'placement' && get(pendingPlacement)) {
      discardPlacement()
    }
  })
</script>
